import React, {memo, useState, useRef, useEffect, useContext, FunctionComponent, MouseEvent} from 'react';
import './EntryCard.less';
import {CSSTransition} from 'react-transition-group';

import {QuickEntryCard} from '../quick-entry-card/QuickEntryCard.tsx';
import {Button} from 'elements/button/Button';

import {EntryImage} from 'elements/entry-image/EntryImage.tsx';
import {EntryEdit} from '../entry-edit/EntryEdit.tsx';
import {getDefaultModifierSelections} from 'helpers/modifier.helper';
import {TranslationsContext} from 'contexts/translations-context';
import {getSafeTranslation} from 'helpers/localization.helper.ts';
import {getModifierValidationErrors} from 'helpers/modifier.helper';
import {getMenuEntryTotalPrice} from 'helpers/order-line.helper.ts';
import {PageScrollContext} from 'contexts/page-scroll-context.ts';
import {MenuEntryModifierSelections, ModifierGroup, ModifierGroupId} from '../../../helpers/modifier.helper.types';
import {MenuEntryId} from '../../../helpers/menu.helper.types';
import {trackEvent} from "../../../helpers/tracking.helpers";

const MinimumQuantity = 1;

interface EntryCardProps {
	id: MenuEntryId,
	name: string,
	description: string,
	price: number,
	image: string,
	imageThumbnail: string,
	allergens: string,
	isSoldOut: boolean,
	isExpanded: boolean,
	isNotPossibleToOrder: boolean,
	quantity: number;
	modifierGroups: Array<ModifierGroup>,
	modifierSelections: MenuEntryModifierSelections,
	onModifierGroupClick: (e: MouseEvent, modifierGroupId: ModifierGroupId, menuEntryId: MenuEntryId) => void,
	onAddClick: (e: MouseEvent, menuEntryId: MenuEntryId, quantity: number, modifierIds: MenuEntryModifierSelections, modifierValidationErrors: string) => void,
	onCloseExpandedClick: (e: MouseEvent) => void,
	onAllergensClick: (allergens: string, e: MouseEvent) => void,
	onQuantityChange: (quantity) => void;
	cartNotEmpty?: boolean;
}

export const EntryCard =
	memo<EntryCardProps>(({
													id,
													name,
													description,
													price,
													image,
													imageThumbnail,
													allergens,
													isSoldOut,
													isExpanded,
													isNotPossibleToOrder,
													quantity,
													modifierGroups,
													modifierSelections,
													onModifierGroupClick,
													onAddClick,
													onCloseExpandedClick,
													onAllergensClick,
													onQuantityChange,
													cartNotEmpty
												}) => {

		const container = useRef(null);
		const [isPlaceholder, setPlaceholder] = useState(true);

		const defaultModifierSelections = getDefaultModifierSelections(modifierGroups);

		const modifierIds = {
			...defaultModifierSelections,
			...modifierSelections
		};

		const translations = useContext(TranslationsContext);

		useEffect(() => {
			//sticky element must have top: -1px;
			const observer = new IntersectionObserver(([e]) => {

				if (e.intersectionRatio <= 0.01 && !isPlaceholder) {
					//setPlaceholder(true);
				} else if (e.intersectionRatio > 0.01 && isPlaceholder) {
					//setPlaceholder(false);
				}
			}, {threshold: [0, 1]});

			observer.observe(container.current);

			return () => {
				observer.unobserve(container.current);
			};
		}, [isPlaceholder]);

		const pageScroll = useContext(PageScrollContext);
		useEffect(() => {
			if (isExpanded) {
				setTimeout(() => {
					const entryCardBottom = container.current?.getBoundingClientRect().bottom;
					const heightAdjustment = cartNotEmpty ? 60 : 20;
					pageScroll.alignByElementBottom(entryCardBottom + heightAdjustment);
				}, 400);

			}
		}, [isExpanded]);

		//if(isPlaceholder && !isExpanded){
		//return <div className="entry-card entry-card--placeholder" ref={container}></div>
		//}

		return (
			<div className={`entry-card ${isExpanded ? 'entry-card--expanded' : ''}`} ref={container}>
				{
					image && (
						<CSSTransition in={isExpanded} classNames="entry-card__big-image" timeout={800} mountOnEnter unmountOnExit>
							<div className="entry-card__big-image" onClick={onCloseExpandedClick}>
								<EntryImage src={image} visibleByDefault/>
							</div>
						</CSSTransition>)
				}
				<div className="entry-card__quick" onClick={onCloseExpandedClick}>
					<QuickEntryCard
						name={name}
						description={description}
						price={getMenuEntryTotalPrice(price, quantity, modifierGroups, modifierIds)}
						image={imageThumbnail || image}
						allergens={allergens}
						isExpanded={isExpanded}
						isSoldOut={isSoldOut}
						isNotPossibleToOrder={isNotPossibleToOrder}
						onAllergensClick={onAllergensClick}
					/>
				</div>
				{
					!isNotPossibleToOrder && (
						<CSSTransition in={isExpanded} classNames="entry-card__details" timeout={300} mountOnEnter unmountOnExit>
							<div className="entry-card__details">
								<div className="entry-card__modifier-list">
									<EntryEdit
										quantity={quantity}
										onQuantityDecClick={e => {
											e.stopPropagation();
											onQuantityChange(quantity > MinimumQuantity ? quantity - 1 : MinimumQuantity);
										}}
										onQuantityIncClick={e => {
											e.stopPropagation();
											onQuantityChange(quantity + 1);
										}}
										modifierGroups={modifierGroups}
										modifierSelections={modifierIds}
										onModifierGroupClick={(e, modifierGroupId) => onModifierGroupClick(e, modifierGroupId, id)}
									/>
								</div>
								<div className="entry-card__add-button">
									<Button
										onClick={e => {
											onAddClick(e, id, quantity, modifierIds, getModifierValidationErrors(modifierIds, modifierGroups, translations));
										}}
										isDisabled={quantity <= 0 || isSoldOut}
									>
										{
											isSoldOut
												? getSafeTranslation('EntryCard.soldOutButtonCaption', translations)
												: getSafeTranslation('EntryCard.addToCartButtonCaption', translations)
										}
									</Button>
								</div>
							</div>
						</CSSTransition>)
				}

			</div>
		);
	});
